import { datadogRum } from '@datadog/browser-rum';
import find from 'lodash/find';
import { normalize } from 'normalizr';
import { push } from 'react-router-redux';
import get from 'lodash/get';

import {
  AUTH_DISCARD_USER,
  AUTH_SET_USER,
  AUTH_UNSET_FIRST_LOGIN,
  CHANGE_SHOP,
  IMPERSONATE_USER,
  RESET_PASSWORD,
  REVERSE_USER,
  SET_BARBER_USER,
  SIGN_UP_ERROR,
  USER_TYPES,
  SET_FRONTEND_FLAGS,
  AUTH_SET_ADMIN_USER,
  SET_PERMISSION_PASSCODE,
  SET_BRAND,
  RESET_SHOP,
  DISMISS_MAINTENANCE_MESSAGE,
  PERMISSION_PASSCODE_LOADING,
  PERMISSION_PASSCODE_LOADED,
} from './constants';
import Http from '../http';
import HttpV2 from '../httpv2';
import { GET_BARBERS } from '../main/barbers/constants';
import * as schema from '../main/barbers/schema';
import { ChurnZeroService } from '../../modules/infra/observability/churnzero';
import { persistentKVStore, sessionKVStore } from '../../modules/infra/persistence';
import { setSessionData } from '../../modules/infra/session';
import { MixpanelTracker } from '../../modules/infra/observability/mixpanel';
import { PostHogTracker } from '../../modules/infra/observability/posthog';

export const setUser = (user, token) => {
  datadogRum.setUser({
    id: user.id,
    username: user.username,
    kind: user.kind,
  });

  MixpanelTracker.getInstance().identify(user.id, user.username);
  PostHogTracker.getInstance().identify(user.id, user.username);

  return {
    type: AUTH_SET_USER,
    token,
    user,
  };
};

const setAdminUser = (user) => ({
  type: AUTH_SET_ADMIN_USER,
  user,
});

const setShop = (shop) => ({
  type: CHANGE_SHOP,
  shop,
});

const resetShop = () => ({
  type: RESET_SHOP,
});

const setBarberUser = (barber) => ({
  type: SET_BARBER_USER,
  barber,
});

const setBarber = (barber) => ({
  type: GET_BARBERS,
  response: normalize([barber], schema.arrayOfBarbers),
});

const setBrand = (brand) => ({
  type: SET_BRAND,
  brand,
});

export const changeBarber = (barberId) => (dispatch) =>
  Http.get(`/barber/${barberId}`, {
    include: 'schedules,shop,shop.address,shop.brand,shop.photos,shop.logo,services,days_on,permissions',
  }).then((barber) => {
    dispatch(setShop(barber.shop));
    dispatch(setBarberUser(barber));
    dispatch(setBarber(barber));
    ChurnZeroService.login(barber.shop.id, barber.id);
  });

export const afterSetUser = (user, dispatch, routeOverrides = {}) => {
  switch (user.kind) {
    case USER_TYPES.shop: {
      if (user.brand) {
        dispatch(setBrand(user.brand));
        ChurnZeroService.logout();
        return dispatch(
          push({
            pathname: '/brand',
            ...routeOverrides,
          })
        );
      }
      const shop = get(user, 'shops[0]');
      dispatch(setShop(shop));
      ChurnZeroService.login(shop.id);
      return dispatch(
        push({
          pathname: '/',
          ...routeOverrides,
        })
      );
    }
    case USER_TYPES.admin:
    case USER_TYPES.regularAdmin:
    case USER_TYPES.agent:
      return dispatch(
        push({
          pathname: '/admin',
          ...routeOverrides,
        })
      );
    case USER_TYPES.barber:
      return dispatch(changeBarber(user.barbers[0].id)).then(() =>
        dispatch(
          push({
            pathname: '/barber',
            ...routeOverrides,
          })
        )
      );
    default:
      // Handle any unhandled user types
      return Promise.resolve();
  }
};

export const changeTempPassword = (username, password) => (dispatch) =>
  Http.post('/user/password-reset-token', { username, password }).then(({ passwordResetToken, kind }) => {
    dispatch(push(`/reset-password/${passwordResetToken}?kind=${kind}`));
  });

const SHOP_INCLUDES = [
  'shops.photos',
  'shops.address',
  'shops.logo',
  'shops.barbers',
  'shops.barbers.photos',
  'shops.schedules',
  'barbers.shop.brand',
  'barbers.shop.brand.extensions',
  'barbers.shop.address',
  'brand',
  'brand.address',
  'brand.extensions',
  'firstShop',
  'permissions',
].join(',');

export const fbeLogin = (username, password) => () => Http.post('/login', { username, password }, false);

export const generateFbeConfig = ({ token }) => {
  const headers = { Authorization: `JWT ${token}` };
  return () => Http.get('/fbe-config', {}, false, headers);
};

const cleanupSession = () => {
  persistentKVStore.removeItem('bankAccountsWarningLastShown');
  sessionKVStore.removeItem('capital@session_token');
  sessionKVStore.removeItem('capital@session_refresh_token');
  ChurnZeroService.logout();
  setSessionData(null);
};

export const signout = () => {
  return (dispatch) =>
    Http.get('/logout')
      .then(() => {
        cleanupSession();
        dispatch({ type: AUTH_DISCARD_USER });
        return dispatch(push('/login'));
      })
      .catch(() => {
        cleanupSession();
        return dispatch(push('/login'));
      });
};

export const checkAdminUser = (dispatch) => {
  const adminToken = persistentKVStore.getItem('adminToken') || false;
  if (!adminToken) {
    return Promise.resolve(null);
  }

  return Http.get('/user/session', {}, true, { Authorization: `JWT ${adminToken}` }).then((user) => {
    dispatch(setAdminUser(user));
    return user;
  });
};

export const checkUser = ({ fromInit = false, shopId = null } = {}) => {
  return (dispatch) =>
    Http.get(`/user/session?include=${SHOP_INCLUDES}`)
      .then((user) => {
        setSessionData(user);
        dispatch(setUser(user, persistentKVStore.getItem('token')));
        const id = shopId || persistentKVStore.getItem('shop');
        switch (user.kind) {
          case USER_TYPES.shop: {
            checkAdminUser(dispatch);
            if (user.brand && !id) {
              ChurnZeroService.logout();
              return dispatch(setBrand(user.brand));
            }
            const shop = find(user.shops, { id }) || get(user, 'shops[0]');
            ChurnZeroService.login(shop.id);
            return dispatch(setShop(shop));
          }

          case USER_TYPES.barber: {
            checkAdminUser(dispatch);
            const barber = find(user.barbers, { shop: { id: shopId } }) || user.barbers[0];
            return dispatch(changeBarber(barber.id));
          }
          default:
            return null;
        }
      })
      .catch((error) => {
        if (error.message === 'Unauthorized') {
          dispatch({ type: AUTH_DISCARD_USER, fromInit });
        }
      });
};

export const changeShop = (shopId) => {
  return (dispatch, getState) => {
    const state = getState();
    const previousActiveType = state.auth.activeType;
    const shops = state.auth.user && state.auth.user.shops;
    if (shops && shops.length) {
      const shop = find(shops, { id: shopId });
      if (shop) {
        dispatch(setShop(shop));
        ChurnZeroService.login(shop.id);
        if (previousActiveType !== 'shop') {
          return dispatch(push('/'));
        }
      }
    }
    return Promise.resolve();
  };
};

export const changeBrand = () => {
  return (dispatch, getState) => {
    const state = getState();
    const brand = state.auth.user && state.auth.user.brand;
    const previousActiveType = state.auth.activeType;
    if (brand) {
      dispatch(resetShop());
      dispatch(setBrand(brand));
      ChurnZeroService.logout();
      if (previousActiveType !== 'brand') {
        return dispatch(push('/brand'));
      }
    }
    return Promise.resolve();
  };
};

export const signUp = (username, password) => {
  return (dispatch) =>
    Http.post('/user', {
      username,
      password,
      kind: 2,
    })
      .then((data) => {
        if (data.message && data.message === 'Unauthorized') {
          console.log('Unauthorized');
        }
      })
      .catch((error) => {
        console.log(error.message);
        dispatch({ type: SIGN_UP_ERROR, signUpError: error.message });
      });
};

export const resetPassword = (username) => {
  return (dispatch) =>
    HttpV2.post('/user/password-reset', {
      username,
    }).then((response) => {
      dispatch({
        type: RESET_PASSWORD,
        payload: {
          username,
          status: response.ok,
        },
      });
      return response;
    });
};

export const verifyPassword = (data) => (dispatch) =>
  Http.post(`/user/password-verify?include=${SHOP_INCLUDES}`, data)
    .then(({ user, token } = {}) => {
      setSessionData(user);
      dispatch(setUser(user, token));
      return afterSetUser(user, dispatch);
    })
    .catch((error) => error.message);

export const validatePassword = (password, kind = null) =>
  Http.post('/user/validate-password', { password, kind }).then(({ isValid, feedback }) => {
    if (isValid) return true;
    return Promise.reject(feedback);
  });

const impersonateUserAction = (user) => ({
  type: IMPERSONATE_USER,
  user,
});

const reverseUserAction = () => ({
  type: REVERSE_USER,
});

export const impersonateUser = (userId, shopId = null) => {
  return async (dispatch, getState) => {
    // get user from api
    const userFromApi = await Http.get(`/user/${userId}`);
    dispatch(impersonateUserAction(userFromApi));
    await dispatch(checkUser({ shopId }));
    const state = getState();
    const brand = state.auth.user && state.auth.user.brand;
    if (!shopId && brand) {
      return dispatch(push('/brand'));
    }

    if (shopId) {
      dispatch(changeShop(shopId));
    }
    return dispatch(push('/'));
  };
};

export const signoutImpersonatedUser = () => {
  return async (dispatch) => {
    dispatch(reverseUserAction());
    await dispatch(checkUser());
    persistentKVStore.removeItem('bankAccountsWarningLastShown');
    sessionKVStore.removeItem('capital@session_token');
    sessionKVStore.removeItem('capital@session_refresh_token');

    ChurnZeroService.logout();

    return dispatch(push('/admin'));
  };
};

export const unsetFirstLogin = () => ({
  type: AUTH_UNSET_FIRST_LOGIN,
});

export const editProfile = (data) => Http.post('/user/edit-profile', data);

const setFrontendFlagsAction = (payload) => ({
  type: SET_FRONTEND_FLAGS,
  payload,
});

export const setFrontendFlags = (frontendFlags) => (dispatch) =>
  editProfile({ frontendFlags }).then(() => dispatch(setFrontendFlagsAction(frontendFlags)));

export const changePassword = (data) => (dispatch, getState) =>
  Http.post('/user/change-password', data).then((response) => {
    const state = getState();
    const currentUser = get(state, 'auth.user');
    dispatch(setUser(currentUser, response.token));
    return dispatch(checkUser());
  });

export const generateShopUserPermissionPasscode = (shopId) => (dispatch) =>
  Http.post(`/shop/${shopId}/generate-permission-passcode`).then((shopUser) =>
    dispatch({
      type: SET_PERMISSION_PASSCODE,
      payload: shopUser.permissionPasscode,
    })
  );

export const setShopUserPermissionPasscode = (shopId, code) => (dispatch) => {
  dispatch({ type: PERMISSION_PASSCODE_LOADING });

  return Http.post(`/shop/${shopId}/set-permission-passcode`, { code })
    .finally(() => dispatch({ type: PERMISSION_PASSCODE_LOADED }))
    .then((shopUser) =>
      dispatch({
        type: SET_PERMISSION_PASSCODE,
        payload: shopUser.permissionPasscode,
      })
    );
};

export const authorizeInstagram = (token, code) =>
  Http.post('/instagram-auth', {
    state: token,
    code,
  });

export const dismissMaintenanceMessage = () => ({
  type: DISMISS_MAINTENANCE_MESSAGE,
});

export const getApiStatus = async () => {
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 8000);

    const response = await Http.get('/status', null, true, null, { signal: controller.signal });

    clearTimeout(id);
    return response;
  } catch (e) {
    return { ok: false };
  }
};
